import React, { useState, useEffect } from 'react';
import { profile as profileApi } from '../services/api';

const ProfilePage = ({ user, onUpdateUser }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    fullname: user?.fullname || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    old_password: '',
    new_password: '',
    new_password_confirmation: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await profileApi.get();
      const userData = res.data.data;
      setFormData(prev => ({
        ...prev,
        fullname: userData.fullname || '',
        email: userData.email || '',
        phone_number: userData.phone_number || '',
      }));
    } catch (err) {
      console.error('Failed to fetch profile', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    setSaving(true);

    try {
      const payload = {
        fullname: formData.fullname,
        email: formData.email,
        phone_number: formData.phone_number,
      };

      if (formData.new_password) {
        payload.old_password = formData.old_password;
        payload.new_password = formData.new_password;
        payload.new_password_confirmation = formData.new_password_confirmation;
      }

      const res = await profileApi.update(payload);
      const updatedUser = res.data.data;
      
      setSuccessMsg('Profil berhasil diperbarui!');
      setFormData(prev => ({
        ...prev,
        old_password: '',
        new_password: '',
        new_password_confirmation: '',
      }));

      // Update localStorage & parent state
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const newUser = { ...savedUser, fullname: updatedUser.fullname, email: updatedUser.email, phone_number: updatedUser.phone_number };
      localStorage.setItem('user', JSON.stringify(newUser));
      if (onUpdateUser) {
        onUpdateUser(newUser);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Gagal memperbarui profil.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manajemen Profil</h2>
          <p className="text-gray-500 text-sm mt-1">Kelola data pribadi dan kata sandi akun Anda.</p>
        </div>
        <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold uppercase rounded-full border border-blue-200">
          {user?.role}
        </span>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div className="bg-green-50 text-green-700 border border-green-200 rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {errorMsg}
        </div>
      )}

      {/* Profile Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informasional / Role & Station */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase">Role Akun</label>
                <p className="text-sm font-bold text-gray-800 capitalize mt-0.5">{user?.role || '-'}</p>
              </div>
              {user?.station_name && (
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase">AHASS Station</label>
                  <p className="text-sm font-bold text-blue-600 mt-0.5">{user.station_name}</p>
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-bold text-gray-900">Informasi Pribadi</h3>
              
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nama Lengkap</label>
                <input
                  type="text"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nomor Telepon</label>
                  <input
                    type="text"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Ubah Password (Optional) */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-bold text-gray-900">Ubah Kata Sandi <span className="text-xs font-normal text-gray-400">(Opsional)</span></h3>
              
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Kata Sandi Saat Ini</label>
                <input
                  type="password"
                  name="old_password"
                  value={formData.old_password}
                  onChange={handleChange}
                  placeholder="Kosongkan jika tidak ingin mengubah kata sandi"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Kata Sandi Baru</label>
                  <input
                    type="password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleChange}
                    placeholder="Minimal 8 karakter"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Konfirmasi Kata Sandi Baru</label>
                  <input
                    type="password"
                    name="new_password_confirmation"
                    value={formData.new_password_confirmation}
                    onChange={handleChange}
                    placeholder="Ulangi kata sandi baru"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition shadow-sm shadow-blue-200 disabled:opacity-50"
              >
                {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
